import { Controller } from "@hotwired/stimulus";

// Studio d'email no-code : palette de composants à gauche,
// email éditable au centre (clic = sélection, double-clic texte = édition
// inline), panneau de design à droite. L'état (blocs + design global) est
// sérialisé en JSON dans des champs cachés, sauvegardé via le formulaire.
export default class extends Controller {
  static targets = ["canvas", "inspector", "blocksField", "designField", "uploadUrl"];
  static values = { blocks: Array, design: Object, uploadUrl: String };

  connect() {
    this.blocks = Array.isArray(this.blocksValue) ? this.blocksValue : [];
    this.design = Object.assign(
      { bg_color: "#f1f5f9", content_bg: "#ffffff", content_width: 600,
        text_color: "#374151", button_color: "#4f46e5", font: "Arial, Helvetica, sans-serif" },
      this.designValue || {}
    );
    if (this.blocks.length === 0) {
      this.blocks = [
        { type: "heading", text: "Votre titre" },
        { type: "text", text: "Rédigez votre message ici. Cliquez pour modifier." },
        { type: "button", text: "En savoir plus", url: "https://" }
      ];
    }
    this.selected = null;
    this.renderCanvas();
    this.renderInspector();
    this.sync();
  }

  // ---- Palette ----
  add(event) {
    const type = event.currentTarget.dataset.blockType;
    const defaults = {
      heading: { type: "heading", text: "Titre" },
      text: { type: "text", text: "Votre texte." },
      image: { type: "image", src: "", alt: "" },
      button: { type: "button", text: "Cliquez ici", url: "https://", align: "center" },
      divider: { type: "divider" },
      spacer: { type: "spacer", height: 24 },
      columns: { type: "columns", columns: [[{ type: "text", text: "Colonne gauche" }], [{ type: "text", text: "Colonne droite" }]] },
      footer: { type: "footer", text: "Vous recevez cet email car vous êtes inscrit." }
    };
    this.blocks.push(JSON.parse(JSON.stringify(defaults[type] || defaults.text)));
    this.selected = this.blocks.length - 1;
    this.renderCanvas();
    this.renderInspector();
    this.sync();
  }

  select(event) {
    const idx = parseInt(event.currentTarget.dataset.idx, 10);
    if (Number.isNaN(idx)) return;
    this.selected = idx;
    this.highlight();
    this.renderInspector();
  }

  move(event) {
    event.stopPropagation();
    const idx = parseInt(event.currentTarget.dataset.idx, 10);
    const dir = event.currentTarget.dataset.dir === "up" ? -1 : 1;
    const to = idx + dir;
    if (to < 0 || to >= this.blocks.length) return;
    const [b] = this.blocks.splice(idx, 1);
    this.blocks.splice(to, 0, b);
    this.selected = to;
    this.renderCanvas();
    this.renderInspector();
    this.sync();
  }

  remove(event) {
    event.stopPropagation();
    const idx = parseInt(event.currentTarget.dataset.idx, 10);
    this.blocks.splice(idx, 1);
    this.selected = null;
    this.renderCanvas();
    this.renderInspector();
    this.sync();
  }

  selectGlobal() {
    this.selected = null;
    this.highlight();
    this.renderInspector();
  }

  // ---- Canvas ----
  renderCanvas() {
    const d = this.design;
    const inner = this.blocks.map((b, i) => this.renderBlockShell(b, i)).join("");
    this.canvasTarget.innerHTML =
      `<div class="mes-doc" style="background:${d.bg_color};font-family:${d.font}">
         <div class="mes-content" style="background:${d.content_bg};max-width:${d.content_width}px">${inner}</div>
       </div>`;
    this.highlight();
  }

  renderBlockShell(b, i) {
    const tools = `<div class="mes-tools">
        <button type="button" data-action="marketing-email-studio#move" data-idx="${i}" data-dir="up">↑</button>
        <button type="button" data-action="marketing-email-studio#move" data-idx="${i}" data-dir="down">↓</button>
        <button type="button" data-action="marketing-email-studio#remove" data-idx="${i}">🗑</button>
      </div>`;
    return `<div class="mes-block" data-idx="${i}" data-action="click->marketing-email-studio#select">${tools}${this.renderBlock(b, i)}</div>`;
  }

  esc(s) { return (s == null ? "" : String(s)).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c])); }

  margins(b) {
    const mt = b.margin_top !== undefined && b.margin_top !== "" ? `margin-top:${b.margin_top}px;` : "";
    const mb = b.margin_bottom !== undefined && b.margin_bottom !== "" ? `margin-bottom:${b.margin_bottom}px;` : "";
    return mt + mb;
  }

  renderBlock(b, i) {
    const d = this.design;
    const m = this.margins(b);
    const ed = (field, html, tag, extra) =>
      `<${tag} contenteditable="true" data-edit="${field}" data-idx="${i}" style="${extra || ""}"
        data-action="blur->marketing-email-studio#editText">${html}</${tag}>`;
    switch (b.type) {
      case "heading":
        return ed("text", this.esc(b.text), "h1", `font-size:${b.size || 24}px;text-align:${b.align || "left"};color:${b.color || d.text_color};${m}`);
      case "text":
        return ed("text", this.esc(b.text).replace(/\n/g, "<br>"), "div", `font-size:${b.size || 15}px;text-align:${b.align || "left"};color:${b.color || d.text_color};${m}`);
      case "button":
        return `<div style="text-align:${b.align || "center"};${m}"><span class="mes-btn" style="background:${b.button_color || d.button_color}">${ed("text", this.esc(b.text), "span")}</span></div>`;
      case "image":
        return `<div style="${m}">${b.src ? `<img src="${this.esc(b.src)}" style="max-width:100%;display:block">` : `<div class="mes-img-ph">Image (cliquez pour configurer)</div>`}</div>`;
      case "divider":
        return `<hr style="border:none;border-top:1px solid #e5e7eb;${m}">`;
      case "spacer":
        return `<div style="height:${b.height || 24}px"></div>`;
      case "footer":
        return ed("text", this.esc(b.text), "div", `font-size:12px;color:#94a3b8;text-align:${b.align || "center"};${m}`);
      case "columns":
        return `<div class="mes-cols" style="${m}">${(b.columns || []).map((col) => `<div class="mes-col">${(col || []).map((cb) => `<div>${this.esc(cb.text)}</div>`).join("")}</div>`).join("")}</div>`;
      case "component":
        return `<div class="mes-component"><span class="mes-component__tag">🧩 Composant</span> <strong>${this.esc(b.name || "Composant")}</strong><span class="mes-component__lock">verrouillé — modifiable dans « Composants »</span></div>`;
      default:
        return "";
    }
  }

  // Insère un composant réutilisable (bloc référence, non éditable inline).
  addComponentBlock(event) {
    const id = parseInt(event.currentTarget.dataset.componentId, 10);
    const name = event.currentTarget.dataset.componentName;
    this.blocks.push({ type: "component", id, name });
    this.selected = this.blocks.length - 1;
    this.renderCanvas();
    this.renderInspector();
    this.sync();
  }

  editText(event) {
    const idx = parseInt(event.currentTarget.dataset.idx, 10);
    const block = this.blocks[idx];
    if (!block) return;
    const text = event.currentTarget.innerText;
    block.text = text;
    this.sync();
  }

  highlight() {
    this.canvasTarget.querySelectorAll(".mes-block").forEach((el) => {
      el.classList.toggle("is-selected", parseInt(el.dataset.idx, 10) === this.selected);
    });
  }

  // ---- Inspector ----
  renderInspector() {
    const b = this.selected != null ? this.blocks[this.selected] : null;
    if (!b) { this.inspectorTarget.innerHTML = this.designInspector(); return; }
    let html = `<div class="mes-insp-head">${this.label(b.type)}</div>`;
    const field = (lbl, key, type = "text") =>
      `<label class="mes-f"><span>${lbl}</span><input type="${type}" value="${this.esc(b[key])}" data-key="${key}" data-action="input->marketing-email-studio#editProp"></label>`;
    const select = (lbl, key, opts) =>
      `<label class="mes-f"><span>${lbl}</span><select data-key="${key}" data-action="change->marketing-email-studio#editProp">${opts.map((o) => `<option value="${o[1]}" ${b[key] === o[1] ? "selected" : ""}>${o[0]}</option>`).join("")}</select></label>`;

    if (["heading", "text", "footer"].includes(b.type)) {
      html += `<label class="mes-f"><span>Contenu</span><textarea rows="4" data-key="text" data-action="input->marketing-email-studio#editProp">${this.esc(b.text)}</textarea></label>`;
      html += select("Alignement", "align", [["Gauche", "left"], ["Centre", "center"], ["Droite", "right"]]);
      html += field("Couleur du texte", "color", "color");
      if (b.type !== "footer") html += field("Taille (px)", "size", "number");
    }
    if (b.type === "button") {
      html += field("Texte", "text");
      html += field("Lien (URL)", "url");
      html += field("Couleur du bouton", "button_color", "color");
      html += select("Alignement", "align", [["Gauche", "left"], ["Centre", "center"], ["Droite", "right"]]);
    }
    if (b.type === "image") {
      html += `<label class="mes-f"><span>Image</span><input type="file" accept="image/*" data-action="change->marketing-email-studio#upload"></label>`;
      html += field("Ou URL d'image", "src");
      html += field("Lien au clic (URL)", "link");
      html += field("Texte alternatif", "alt");
    }
    if (b.type === "spacer") html += field("Hauteur (px)", "height", "number");
    if (b.type === "columns") {
      html += `<label class="mes-f"><span>Colonne gauche</span><textarea rows="3" data-col="0" data-action="input->marketing-email-studio#editColumn">${this.esc(b.columns?.[0]?.[0]?.text)}</textarea></label>`;
      html += `<label class="mes-f"><span>Colonne droite</span><textarea rows="3" data-col="1" data-action="input->marketing-email-studio#editColumn">${this.esc(b.columns?.[1]?.[0]?.text)}</textarea></label>`;
    }
    if (b.type === "component") {
      const url = (this.element.dataset.componentsEditBase || "").replace("__ID__", b.id);
      html += `<p class="mes-comp-note">Ce composant se modifie dans l'éditeur de composants. Toute modification s'appliquera à tous les emails qui l'utilisent.</p>`;
      if (url) html += `<a class="btn-ds btn-ds--ghost btn-ds--sm" href="${url}" target="_blank">Modifier le composant</a>`;
    } else if (b.type !== "spacer") {
      // Espacement avant / après (sauf spacer qui est lui-même un espace).
      html += `<div class="mes-f-row">${field("Espace avant (px)", "margin_top", "number")}${field("Espace après (px)", "margin_bottom", "number")}</div>`;
    }
    html += `<button type="button" class="btn-ds btn-ds--ghost btn-ds--sm mes-global-btn" data-action="marketing-email-studio#selectGlobal">Réglages globaux</button>`;
    this.inspectorTarget.innerHTML = html;
  }

  designInspector() {
    const d = this.design;
    const f = (lbl, key, type = "color") =>
      `<label class="mes-f"><span>${lbl}</span><input type="${type}" value="${this.esc(d[key])}" data-dkey="${key}" data-action="input->marketing-email-studio#editDesign"></label>`;
    return `<div class="mes-insp-head">Design global</div>
      ${f("Fond de l'email", "bg_color")}
      ${f("Fond du contenu", "content_bg")}
      ${f("Couleur du texte", "text_color")}
      ${f("Couleur des boutons", "button_color")}
      <label class="mes-f"><span>Largeur (px)</span><input type="number" value="${d.content_width}" data-dkey="content_width" data-action="input->marketing-email-studio#editDesign"></label>`;
  }

  label(type) {
    return { heading: "Titre", text: "Texte", button: "Bouton", image: "Image",
             divider: "Séparateur", spacer: "Espaceur", footer: "Footer", columns: "Colonnes" }[type] || type;
  }

  editProp(event) {
    const b = this.blocks[this.selected]; if (!b) return;
    b[event.target.dataset.key] = event.target.value;
    this.renderCanvas();
    this.sync();
  }

  editColumn(event) {
    const b = this.blocks[this.selected]; if (!b) return;
    const col = parseInt(event.target.dataset.col, 10);
    b.columns = b.columns || [[{ type: "text", text: "" }], [{ type: "text", text: "" }]];
    b.columns[col] = [{ type: "text", text: event.target.value }];
    this.renderCanvas();
    this.sync();
  }

  editDesign(event) {
    this.design[event.target.dataset.dkey] = event.target.value;
    this.renderCanvas();
    this.sync();
  }

  upload(event) {
    const file = event.target.files[0];
    if (!file) return;
    const b = this.blocks[this.selected];
    const fd = new FormData();
    fd.append("file", file);
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    fetch(this.uploadUrlValue, { method: "POST", headers: { "X-CSRF-Token": token }, body: fd })
      .then((r) => r.json())
      .then((res) => { if (res.url) { b.src = res.url; this.renderCanvas(); this.renderInspector(); this.sync(); } })
      .catch(() => {});
  }

  sync() {
    this.blocksFieldTarget.value = JSON.stringify(this.blocks);
    this.designFieldTarget.value = JSON.stringify(this.design);
  }
}
